import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyRules, acknowledgeRule } from '../../../../app/features/rulesRegulationsSlice';
import { toast } from 'react-toastify';
import RuleAttachmentViewer from '../Shared/RuleAttachmentViewer';

const MyRulesDashboard = () => {
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state); // Assuming auth contains user details
  const { myRules, loading } = useSelector((state) => state.rulesRegulations || { myRules: {} });

  const [activeTab, setActiveTab] = useState('site');
  const [selectedRule, setSelectedRule] = useState(null);

  useEffect(() => {
    // Replace mock params with actual user context
    const userId = auth?.user?.id || 1;
    const siteId = auth?.user?.siteId || 1;
    const wingId = auth?.user?.wingId || null;
    const floorId = auth?.user?.floorId || null;

    dispatch(fetchMyRules({ userId, siteId, wingId, floorId }));
  }, [dispatch, auth]);

  const handleAcknowledge = async (ruleId, versionId) => {
    const userId = auth?.user?.id || 1;
    const deviceId = "WEB_PORTAL"; // Static identifier for web

    try {
      const response = await dispatch(acknowledgeRule({ id: ruleId, ruleVersionId: versionId, deviceId, userId })).unwrap();

      if (response?.isSuccess || response?.success || response?.IsSuccess) {
        toast.success(response?.message || response?.Message || "Rule acknowledged successfully!");

        // Refresh the list to hide the red dot and show checkmark
        const siteId = auth?.user?.siteId || auth?.user?.SiteId || 1;
        const wingId = auth?.user?.wingId || auth?.user?.WingId || null;
        const floorId = auth?.user?.floorId || auth?.user?.FloorId || null;
        dispatch(fetchMyRules({ userId, siteId, wingId, floorId }));
      } else {
        toast.error(response?.message || response?.Message || "Failed to acknowledge rule.");
      }
    } catch (error) {
      toast.error(typeof error === 'string' ? error : "An error occurred while acknowledging.");
    }
  };

  const renderRuleCard = (rule) => (
    <div key={rule.id || rule.Id} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
      <div className={`h-1.5 w-full ${(rule.priority || rule.Priority) === 'High' ? 'bg-orange-500' : 'bg-indigo-500'}`}></div>
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
            {rule.categoryName || rule.CategoryName}
          </span>
          {(rule.requiresAcknowledgement ?? rule.RequiresAcknowledgement) && !(rule.isAcknowledged ?? rule.IsAcknowledged) && (
            <span className="flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">{rule.title || rule.Title}</h3>
        <p className="text-sm text-gray-600 line-clamp-2 mb-4">{rule.description || rule.Description}</p>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={() => setSelectedRule(rule)}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium focus:outline-none"
          >
            View Details
          </button>

          {(rule.requiresAcknowledgement ?? rule.RequiresAcknowledgement) ? (
            (rule.isAcknowledged ?? rule.IsAcknowledged) ? (
              <span className="text-sm font-medium text-green-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Acknowledged
              </span>
            ) : (
              <button
                onClick={() => {
                  const versionId = rule.latestVersionId || rule.LatestVersionId || rule.currentVersion || rule.CurrentVersion || rule.versionId || rule.VersionId || rule.ruleVersionId || rule.RuleVersionId || 1;
                  handleAcknowledge(rule.id || rule.Id, versionId);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-1.5 px-4 rounded shadow transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Acknowledge
              </button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen relative">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Community Guidelines</h1>
        <p className="text-gray-500 mt-2">Stay informed about the rules and regulations in your community.</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          {['site', 'wing', 'floor'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab} Rules
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {activeTab === 'site' && myRules?.siteRules?.map(renderRuleCard)}
          {activeTab === 'wing' && myRules?.wingRules?.map(renderRuleCard)}
          {activeTab === 'floor' && myRules?.floorRules?.map(renderRuleCard)}

          {(activeTab === 'site' && !myRules?.siteRules?.length) ||
            (activeTab === 'wing' && !myRules?.wingRules?.length) ||
            (activeTab === 'floor' && !myRules?.floorRules?.length) ? (
            <div className="col-span-full py-16 text-center bg-white rounded-xl border border-gray-200 border-dashed">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">No {activeTab} rules found.</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Detail Modal Overlay */}
      {selectedRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-fadeIn">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">{selectedRule.title || selectedRule.Title}</h2>
              <button onClick={() => setSelectedRule(null)} className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center space-x-4 mb-6 text-sm">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full font-medium">{selectedRule.categoryName || selectedRule.CategoryName}</span>
                <span className="text-gray-500">Effective: {new Date(selectedRule.effectiveFrom || selectedRule.EffectiveFrom).toLocaleDateString()}</span>
              </div>
              <div className="prose max-w-none text-gray-700 mb-6 whitespace-pre-wrap">
                {selectedRule.description || selectedRule.Description}
              </div>

              {(() => {
                const nested = (selectedRule.attachments || selectedRule.Attachments || [])[0];
                const fileUrl = selectedRule.attachmentUrl || selectedRule.AttachmentUrl || nested?.fileUrl || nested?.FileUrl;
                const fileName = selectedRule.attachmentName || selectedRule.AttachmentName || nested?.fileName || nested?.FileName || 'Document';
                const fileType = nested?.fileType || nested?.FileType || (
                  /\.pdf($|\?)/i.test(fileUrl || fileName) ? 'application/pdf'
                    : /\.(png|jpe?g|gif|webp)($|\?)/i.test(fileUrl || fileName) ? 'image/jpeg'
                    : ''
                );
                if (!fileUrl) return null;
                return <RuleAttachmentViewer attachment={{ fileUrl, fileType, fileName }} />;
              })()}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button onClick={() => setSelectedRule(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors">
                Close
              </button>
              {(selectedRule.requiresAcknowledgement ?? selectedRule.RequiresAcknowledgement) && !(selectedRule.isAcknowledged ?? selectedRule.IsAcknowledged) && (
                <button
                  onClick={() => {
                    const versionId = selectedRule.latestVersionId || selectedRule.LatestVersionId || selectedRule.currentVersion || selectedRule.CurrentVersion || selectedRule.versionId || selectedRule.VersionId || selectedRule.ruleVersionId || selectedRule.RuleVersionId || 1;
                    handleAcknowledge(selectedRule.id || selectedRule.Id, versionId);
                    setSelectedRule(null);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded shadow transition-colors"
                >
                  Acknowledge Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRulesDashboard;
